import { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../theme/pageCardSx';

import DateRangeSelector from '../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../components/FilterSelectorWithApp';
import PointhomeDashboard from '../../components/pointhome/PointhomeDashboard';
import PointhomeDailyReportTable from '../../components/pointhome/PointhomeDailyReportTable';
import PointhomeAppDeltaTop5 from '../../components/pointhome/PointhomeAppDeltaTop5';
import PointhomeServiceRevenue from '../../components/pointhome/PointhomeServiceRevenue';
import PointhomeAppRevenueTop5 from '../../components/pointhome/PointhomeAppRevenueTop5';

import type { PointhomeData } from '../../types/pointhome.types';

export default function Pointhome() {
  const [dailyFigureData, setDailyFigureData] = useState<PointhomeData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [tab, setTab] = useState<number>(0);

  const fetchFilterOptions = useCallback(() => {
    apiClient.get(`/api/v1/stat/pointhome/app/list`).then((response) => {
      setAppOptions(['앱 전체', ...response.data]);
    }).catch(() => {
      // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
    });
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchDailyFigureData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } : {}),
    };

    apiClient
      .get(`/api/v1/stat/pointhome/summary`, { params })
      .then((response) => {
        setDailyFigureData(response.data);
        setErrorMessage('');
      })
      .catch(() => {
        setDailyFigureData(null);
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) {
      fetchDailyFigureData();
    }
  }, [initialized, startDate, endDate, selectedApp, fetchDailyFigureData]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      {/* 상단 필터 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) fetchDailyFigureData();
            }}
          />
        </Box>

        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(v) => setSelectedApp(v)}
            onSearch={fetchDailyFigureData}
          />
        </Box>
      </Box>

      {/* 대시보드 박스 */}
      <Box
        sx={{ ...PAGE_CARD_SX, width: '100%' }}
      >
        {/* 앱 선택 (Chip) - 항상 렌더링되어 스크롤 위치 유지 */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: 3 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#cbd5e1' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 600 : 500,
                backgroundColor: selectedApp === name ? '#0044D2' : '#f8fafc',
                color: selectedApp === name ? '#fff' : '#475569',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e2e8f0'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f1f5f9',
                  borderColor: selectedApp === name ? '#0033a0' : '#cbd5e1',
                },
                height: 36,
                fontSize: '0.85rem',
                borderRadius: 2,
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#f1f5f9', mb: 2 }} />

        {/* 로딩/에러/콘텐츠 분기 처리 */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <PointhomeDashboard data={dailyFigureData} selectedApp={selectedApp} />
        )}
      </Box>

      {/* 일별 리포트 */}
      {!loading && !errorMessage && (
        <Box
          sx={{ ...PAGE_CARD_SX, width: '100%' }}
        >
          <Tabs
            value={0}
            sx={{
              minHeight: 44,
              mb: 2,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#64748b',
                '&.Mui-selected': { color: '#0044D2' },
              },
              '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
            }}
          >
            <Tab value={0} label="일별 레포트" />
          </Tabs>
          {startDate && endDate && (
            <PointhomeDailyReportTable 
              data={dailyFigureData?.dailyRevenue ?? []}
              selectedApp={selectedApp}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </Box>
      )}

      {/* 앱별/서비스별 매출 */}
      {!loading && !errorMessage && (
        <Box
          sx={{ ...PAGE_CARD_SX, width: '100%' }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 44,
              mb: 2,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#64748b',
                '&.Mui-selected': { color: '#0044D2' },
              },
              '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
            }}
          >
            <Tab value={0} label="앱별 매출" />
            <Tab value={1} label="앱별 매출 순위" />
            <Tab value={2} label="서비스별 매출" />    
          </Tabs>

          {tab === 0 && (
            <PointhomeAppDeltaTop5
              topUps={dailyFigureData?.appDeltaTop5?.topUps ?? []}
              topDowns={dailyFigureData?.appDeltaTop5?.topDowns ?? []}
            />
          )}

          {tab === 2 && (
            <PointhomeServiceRevenue 
              data={dailyFigureData?.dailyRevenue ?? []} 
            />
          )}

          {tab === 1 && (
            <PointhomeAppRevenueTop5 
              data={dailyFigureData?.appRevenueTop5 ?? []} 
            />
          )}
        </Box>
      )}
    </Box>
  );
}
